import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { queryKeyFactory } from 'utils/query-client';
import { loaders } from './integrations.loaders';
import { converters } from './integrations.converters';
import {
  ApiIntegrationLogsFilters,
  IntegrationLogItemsFilters,
  IntegrationLogsFilters,
} from './integrations.types';

export const integrationsKeys = queryKeyFactory('integrations');

export const useIntegrations = () => {
  return useQuery({
    queryKey: integrationsKeys.list(),
    queryFn: () => loaders.getIntegrations(),
    select: converters.getIntegrations,
  });
};

export const useIntegration = (integrationId: string) => {
  return useQuery({
    queryKey: integrationsKeys.detail(integrationId),
    queryFn: () => loaders.getIntegration({ integrationId }),
    select: converters.getIntegration,
    enabled: !!integrationId,
  });
};

export const useIntegrationModels = () => {
  return useQuery({
    queryKey: integrationsKeys.list('models'),
    queryFn: () => loaders.getIntegrationModels(),
    select: converters.getIntegrationModels,
  });
};

export const useIntegrationModel = (integrationModelId: string) => {
  return useQuery({
    queryKey: integrationsKeys.detail('models', integrationModelId),
    queryFn: () => loaders.getIntegrationModel({ integrationModelId }),
    select: converters.getIntegrationModel,
    enabled: !!integrationModelId,
  });
};

export const useIntegrationLogs = (
  integrationModelId: string,
  filters: Partial<IntegrationLogsFilters> = {},
) => {
  return useQuery({
    queryKey: integrationsKeys.list('logs', integrationModelId, filters),
    queryFn: () => loaders.getIntegrationLogs({ integrationModelId, filters }),
    select: converters.getIntegrationLogs,
    enabled: !!integrationModelId,
  });
};

export const useIntegrationLogItems = (
  processId: string,
  filters: Partial<IntegrationLogItemsFilters> = {},
) => {
  return useQuery({
    queryKey: integrationsKeys.list('log-items', processId),
    queryFn: () => loaders.getIntegrationLogItems({ processId, filters }),
    select: converters.getIntegrationLogItems,
    enabled: !!processId,
  });
};

export const useIntegrationLogEvents = (
  integrationId: string,
  filters: Partial<ApiIntegrationLogsFilters> = {},
) => {
  return useInfiniteQuery({
    queryKey: integrationsKeys.list('api-logs', integrationId, filters),
    queryFn: ({ pageParam }) =>
      loaders.getIntegrationLogEvents({
        integrationId,
        filters,
        nextToken: pageParam,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const nextToken = lastPage.data.nextToken?.trim();
      if (!nextToken || !lastPage.data.events.length) {
        return undefined;
      }

      const tokenAlreadyUsed = allPages
        .slice(0, -1)
        .some((page) => page.data.nextToken === nextToken);

      return tokenAlreadyUsed ? undefined : nextToken;
    },
    select: ({ pages, pageParams }) => ({
      pages: pages.map((page) => converters.getIntegrationLogEvents(page)),
      pageParams,
    }),
    enabled: !!integrationId,
    keepPreviousData: true,
  });
};
